import { useEffect, useState } from "react";
import { api } from "../../api";
import { useSession } from "../../shell/session";
import type { Group, OutboxMessage } from "../../../shared/types";
import { Badge, Empty, ErrorNote } from "../../ui";
import "../people/community.css";
import { canCoordinate, errorText, safeWhen, usePageTitle } from "../people/support";

interface MessageResult {
  message: OutboxMessage;
  delivery: string;
}

const CHANNELS: Array<OutboxMessage["channel"]> = ["email", "sms", "in-app"];

function statusCopy(message: OutboxMessage): string {
  if (message.status === "draft") return "Draft only. Nothing has been queued or sent.";
  if (message.status === "queued-local") return "Stored in the local outbox. This does not post a notification or leave this computer.";
  return "Not delivered. Email and SMS providers are not configured.";
}

function toneFor(status: OutboxMessage["status"]): "mute" | "amber" | "danger" {
  if (status === "not-delivered") return "danger";
  if (status === "queued-local") return "amber";
  return "mute";
}

export function MessagesPage() {
  const { user } = useSession();
  const allowed = canCoordinate(user?.role);
  const [messages, setMessages] = useState<OutboxMessage[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [channel, setChannel] = useState<OutboxMessage["channel"]>("email");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  const [delivery, setDelivery] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(allowed);
  usePageTitle("Outbox");

  useEffect(() => {
    if (!allowed) return;
    let live = true;
    Promise.all([
      api<OutboxMessage[]>("/api/v1/messages"),
      api<Group[]>("/api/v1/groups"),
    ]).then(([rows, groupRows]) => {
      if (!live) return;
      setMessages(rows);
      setGroups(groupRows);
      setError("");
    }).catch((caught: unknown) => { if (live) setError(errorText(caught)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [allowed]);

  const selected = messages.find((message) => message.id === selectedId) ?? null;
  const draftPreview: OutboxMessage = {
    id: "draft-preview",
    channel,
    subject,
    body,
    audience,
    status: "draft",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  const preview = showSaved && selected ? selected : draftPreview;

  async function save(queue: boolean) {
    if (!body.trim()) {
      setError("Write the message first.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await api<MessageResult>("/api/v1/messages", {
        method: "POST",
        body: JSON.stringify({ channel, subject: subject.trim(), body: body.trim(), audience: audience.trim(), queue }),
      });
      setMessages((current) => [saved.message, ...current.filter((item) => item.id !== saved.message.id)]);
      setSelectedId(saved.message.id);
      setShowSaved(true);
      setDelivery(saved.delivery);
      setSubject("");
      setBody("");
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setSaving(false);
    }
  }

  if (!allowed) {
    return (
      <section className="page">
        <header className="page-head">
          <div>
            <p className="kicker">Outbox</p>
            <h1 className="page-title">Messages</h1>
          </div>
        </header>
        <p className="mbc-limit">The outbox is open to coordinators, leaders, and owners. This login cannot read or write drafts.</p>
      </section>
    );
  }

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="kicker">Outbox</p>
          <h1 className="page-title">Messages</h1>
          <p className="lede">Drafts and a local outbox. Email and SMS are not connected, so nothing leaves this computer.</p>
        </div>
      </header>
      <div className="mbc-provider" role="status">
        <strong>No email or SMS provider.</strong>
        <span>Saving a draft keeps it here. Queuing an email or SMS marks it not delivered. Queuing in-app does not create a notification.</span>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {delivery && <p className="notice" role="status">{delivery}</p>}
      <div className="mbc-compose">
        <form className="panel panel-pad form-grid" onSubmit={(event) => { event.preventDefault(); void save(false); }}>
          <h2>Compose</h2>
          <div className="field">
            <label htmlFor="msg-channel">Channel</label>
            <select id="msg-channel" value={channel} onChange={(event) => setChannel(event.target.value as OutboxMessage["channel"])}>
              {CHANNELS.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="msg-audience">Audience label</label>
            <input id="msg-audience" list="audience-labels" value={audience} onChange={(event) => setAudience(event.target.value)} maxLength={200} placeholder="Sunday band" />
            <datalist id="audience-labels">{groups.map((group) => <option key={group.id} value={group.name} />)}</datalist>
            <p className="faint">This is a label for the draft. It does not choose inboxes or phone numbers.</p>
          </div>
          <div className="field"><label htmlFor="msg-subject">Subject</label><input id="msg-subject" value={subject} onChange={(event) => setSubject(event.target.value)} maxLength={180} placeholder={channel === "sms" ? "Optional for SMS" : "Subject"} /></div>
          <div className="field"><label htmlFor="msg-body">Message</label><textarea id="msg-body" value={body} onChange={(event) => setBody(event.target.value)} maxLength={8000} required /></div>
          <div className="cluster">
            <button className="btn primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Save draft"}</button>
            <button className="btn" type="button" disabled={saving} onClick={() => void save(true)}>Queue locally</button>
          </div>
          <p className="faint">Saved messages cannot be edited or deleted. Write another draft if this one should change.</p>
        </form>
        <Letter message={preview} heading={showSaved && selected ? "Saved message" : "Draft preview"} onShowDraft={selected ? () => setShowSaved(false) : undefined} saved={showSaved && Boolean(selected)} />
      </div>
      <article className="panel panel-pad">
        <h2>Local outbox</h2>
        {loading && <p className="muted">Loading drafts…</p>}
        {!loading && messages.length === 0 && <Empty title="Outbox is empty" body="A saved draft will appear here. Nothing is sent." />}
        {messages.map((message) => (
          <button key={message.id} type="button" className={message.id === selectedId ? "mbc-message active" : "mbc-message"} onClick={() => { setSelectedId(message.id); setShowSaved(true); }}>
            <span className="split">
              <strong>{message.subject || "(no subject)"}</strong>
              <Badge tone={toneFor(message.status)}>{message.status}</Badge>
            </span>
            <span className="faint">{message.channel} · {message.audience || "No audience label"} · {safeWhen(message.updatedAt)}</span>
          </button>
        ))}
      </article>
    </section>
  );
}

function Letter({ message, heading, saved, onShowDraft }: { message: OutboxMessage; heading: string; saved: boolean; onShowDraft?: () => void }) {
  return (
    <article className="mbc-letter" aria-label={heading}>
      <div className="split">
        <p className="kicker">{heading}</p>
        {saved && onShowDraft && <button className="btn small ghost" type="button" onClick={onShowDraft}>Show draft</button>}
      </div>
      <div className="cluster">
        <Badge tone="violet">{message.channel}</Badge>
        <Badge tone={toneFor(message.status)}>{message.status}</Badge>
      </div>
      <h2>{message.subject.trim() || "No subject"}</h2>
      <p className="muted">Audience label: {message.audience.trim() || "Not set"}</p>
      <p className="mbc-body">{message.body.trim() || "The message body will appear here as you write."}</p>
      <p className="notice">{statusCopy(message)}</p>
    </article>
  );
}
